import { supabase } from '../lib/supabase.js';

/**
 * MilestoneCompletionDetectionService
 * 
 * Intelligently detects when milestones have been completed based on
 * actual data changes in the system.
 */
class MilestoneCompletionDetectionService {
  /**
   * Completion rules map milestone types to detection functions
   */
  static COMPLETION_RULES = {
    // College selection rules
    'select_reach_schools': async (userId, metadata) => {
      const targetCount = metadata?.targetCount || 4;
      const { data: colleges } = await supabase
        .from('college_journey_colleges')
        .select('id')
        .eq('user_id', userId)
        .eq('category', 'reach');
      
      return {
        isComplete: colleges?.length >= targetCount,
        progress: `${colleges?.length || 0}/${targetCount} reach schools selected`,
        data: { count: colleges?.length || 0, target: targetCount }
      };
    },

    'select_target_schools': async (userId, metadata) => {
      const targetCount = metadata?.targetCount || 4;
      const { data: colleges } = await supabase
        .from('college_journey_colleges')
        .select('id')
        .eq('user_id', userId)
        .eq('category', 'target');
      
      return {
        isComplete: colleges?.length >= targetCount,
        progress: `${colleges?.length || 0}/${targetCount} target schools selected`,
        data: { count: colleges?.length || 0, target: targetCount }
      };
    },

    'select_safety_schools': async (userId, metadata) => {
      const targetCount = metadata?.targetCount || 3;
      const { data: colleges } = await supabase
        .from('college_journey_colleges')
        .select('id')
        .eq('user_id', userId)
        .eq('category', 'safety');
      
      return {
        isComplete: colleges?.length >= targetCount,
        progress: `${colleges?.length || 0}/${targetCount} safety schools selected`,
        data: { count: colleges?.length || 0, target: targetCount }
      };
    },

    'complete_college_list': async (userId, metadata) => {
      const { data: colleges } = await supabase
        .from('college_journey_colleges')
        .select('category')
        .eq('user_id', userId);
      
      const byCategory = colleges?.reduce((acc, c) => {
        acc[c.category] = (acc[c.category] || 0) + 1;
        return acc;
      }, {}) || {};

      const hasBalanced = byCategory.reach >= 3 && 
                         byCategory.target >= 3 && 
                         byCategory.safety >= 2;
      
      return {
        isComplete: hasBalanced,
        progress: `Reach: ${byCategory.reach || 0}, Target: ${byCategory.target || 0}, Safety: ${byCategory.safety || 0}`,
        data: byCategory
      };
    },

    // Financial aid rules
    'complete_fafsa': async (userId, metadata) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('fafsa_submitted, fafsa_submission_date')
        .eq('id', userId)
        .single();
      
      return {
        isComplete: profile?.fafsa_submitted === true,
        progress: profile?.fafsa_submitted ? 'FAFSA submitted' : 'FAFSA not submitted',
        data: { 
          submitted: profile?.fafsa_submitted,
          date: profile?.fafsa_submission_date 
        }
      };
    },

    'complete_css_profile': async (userId, metadata) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('css_profile_submitted, css_submission_date')
        .eq('id', userId)
        .single();
      
      return {
        isComplete: profile?.css_profile_submitted === true,
        progress: profile?.css_profile_submitted ? 'CSS Profile submitted' : 'CSS Profile not submitted',
        data: { 
          submitted: profile?.css_profile_submitted,
          date: profile?.css_submission_date 
        }
      };
    },

    // Test score rules
    'submit_test_scores': async (userId, metadata) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('sat_score, act_score')
        .eq('id', userId)
        .single();
      
      const hasScores = profile?.sat_score || profile?.act_score;
      
      return {
        isComplete: hasScores ? true : false,
        progress: hasScores ? 'Test scores recorded' : 'No test scores on file',
        data: { 
          sat: profile?.sat_score,
          act: profile?.act_score
        }
      };
    },

    // Essay rules
    'complete_personal_statement': async (userId, metadata) => {
      const { data: essays } = await supabase
        .from('college_journey_essays')
        .select('status')
        .eq('user_id', userId)
        .eq('type', 'personal_statement')
        .single();
      
      const isComplete = essays?.status === 'complete' || essays?.status === 'submitted';
      
      return {
        isComplete,
        progress: essays ? `Personal statement: ${essays.status}` : 'Personal statement not started',
        data: { status: essays?.status }
      };
    },

    'complete_supplemental_essays': async (userId, metadata) => {
      const collegeId = metadata?.collegeId;
      
      let query = supabase
        .from('college_journey_essays')
        .select('status')
        .eq('user_id', userId)
        .eq('type', 'supplemental');
      
      if (collegeId) {
        query = query.eq('college_id', collegeId);
      }
      
      const { data: essays } = await query;
      
      const total = essays?.length || 0;
      const complete = essays?.filter(e => 
        e.status === 'complete' || e.status === 'submitted'
      ).length || 0;
      
      return {
        isComplete: total > 0 && complete === total,
        progress: `${complete}/${total} supplemental essays complete`,
        data: { complete, total }
      };
    },

    // Application submission rules
    'submit_application': async (userId, metadata) => {
      const collegeId = metadata?.collegeId;
      
      if (!collegeId) {
        return {
          isComplete: false,
          progress: 'No college specified',
          data: {}
        };
      }
      
      const { data: college } = await supabase
        .from('college_journey_colleges')
        .select('application_status, application_submitted_date')
        .eq('id', collegeId)
        .eq('user_id', userId)
        .single();
      
      const isSubmitted = college?.application_status === 'submitted';
      
      return {
        isComplete: isSubmitted,
        progress: isSubmitted ? 'Application submitted' : `Application status: ${college?.application_status || 'not started'}`,
        data: { 
          status: college?.application_status,
          date: college?.application_submitted_date
        }
      };
    },

    // Recommendation letters
    'request_recommendations': async (userId, metadata) => {
      const targetCount = metadata?.targetCount || 2;
      const { data: recs } = await supabase
        .from('recommendation_requests')
        .select('id, status')
        .eq('student_id', userId);
      
      const requested = recs?.length || 0;
      const received = recs?.filter(r => r.status === 'received').length || 0;
      
      return {
        isComplete: requested >= targetCount,
        progress: `${requested} requested, ${received} received (need ${targetCount})`,
        data: { requested, received, target: targetCount }
      };
    }
  };

  /**
   * Check if a milestone is complete based on its rule
   */
  static async checkMilestoneCompletion(milestone, userId) {
    try {
      // Extract rule type from milestone
      const ruleType = milestone.completion_rule || milestone.metadata?.completion_rule;
      
      if (!ruleType || !this.COMPLETION_RULES[ruleType]) {
        return {
          isComplete: false,
          progress: 'Manual completion required',
          canAutoComplete: false
        };
      }

      // Run the completion check
      const result = await this.COMPLETION_RULES[ruleType](userId, milestone.metadata);
      
      return {
        ...result,
        canAutoComplete: true,
        ruleType
      };
    } catch (error) {
      console.error('Error checking milestone completion:', error);
      return {
        isComplete: false,
        progress: 'Error checking completion',
        canAutoComplete: false,
        error: error.message
      };
    }
  }

  /**
   * Check all milestones for a user and return completion status
   */
  static async checkAllMilestones(userId) {
    try {
      // Get all incomplete milestones for the user
      const { data: milestones, error } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .eq('source_type', 'milestone_template')
        .neq('workflow_phase', 'complete');

      if (error) throw error;

      const results = [];
      
      for (const milestone of milestones || []) {
        const completion = await this.checkMilestoneCompletion(milestone, userId);
        
        if (completion.canAutoComplete) {
          results.push({
            milestoneId: milestone.id,
            title: milestone.title,
            ...completion
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error checking all milestones:', error);
      throw error;
    }
  }

  /**
   * Suggest milestone completion to user
   */
  static async suggestCompletion(milestoneId, userId) {
    try {
      // Get the milestone
      const { data: milestone, error } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('id', milestoneId)
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      if (!milestone) throw new Error('Milestone not found');

      // Check completion
      const completion = await this.checkMilestoneCompletion(milestone, userId);

      if (completion.isComplete) {
        // Return suggestion data
        return {
          shouldSuggest: true,
          milestone,
          completion,
          message: `It looks like you've completed "${milestone.title}". Would you like to mark it as complete?`,
          action: 'mark_complete'
        };
      }

      return {
        shouldSuggest: false,
        milestone,
        completion,
        message: completion.progress
      };
    } catch (error) {
      console.error('Error suggesting completion:', error);
      throw error;
    }
  }

  /**
   * Auto-complete milestone if conditions are met
   */
  static async autoCompleteMilestone(milestoneId, userId) {
    try {
      const suggestion = await this.suggestCompletion(milestoneId, userId);
      
      if (!suggestion.shouldSuggest) {
        return {
          success: false,
          message: 'Milestone not ready for completion',
          progress: suggestion.completion.progress
        };
      }

      // Update the milestone to complete
      const { error: updateError } = await supabase
        .from('timeline_items')
        .update({
          workflow_phase: 'complete',
          completion_status: 'completed',
          completion_date: new Date().toISOString(),
          metadata: {
            ...suggestion.milestone.metadata,
            auto_completed: true,
            completion_data: suggestion.completion.data
          }
        })
        .eq('id', milestoneId)
        .eq('user_id', userId);

      if (updateError) throw updateError;

      return {
        success: true,
        message: `Milestone "${suggestion.milestone.title}" marked as complete`,
        completion: suggestion.completion
      };
    } catch (error) {
      console.error('Error auto-completing milestone:', error);
      throw error;
    }
  }

  /**
   * Register a data change event that might affect milestones
   */
  static async handleDataChange(eventType, data) {
    try {
      const userId = data.user_id || data.student_id;
      if (!userId) return;

      // Map event types to relevant completion rules
      const eventRuleMap = {
        'college_added': ['select_reach_schools', 'select_target_schools', 'select_safety_schools', 'complete_college_list'],
        'college_removed': ['select_reach_schools', 'select_target_schools', 'select_safety_schools', 'complete_college_list'],
        'fafsa_updated': ['complete_fafsa'],
        'css_updated': ['complete_css_profile'],
        'test_scores_updated': ['submit_test_scores'],
        'essay_updated': ['complete_personal_statement', 'complete_supplemental_essays'],
        'application_submitted': ['submit_application'],
        'recommendation_requested': ['request_recommendations']
      };

      const relevantRules = eventRuleMap[eventType] || [];
      
      // Check milestones that might be affected
      const { data: milestones } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', userId)
        .eq('source_type', 'milestone_template')
        .neq('workflow_phase', 'complete')
        .in('completion_rule', relevantRules);

      const suggestions = [];
      
      for (const milestone of milestones || []) {
        const suggestion = await this.suggestCompletion(milestone.id, userId);
        if (suggestion.shouldSuggest) {
          suggestions.push(suggestion);
        }
      }

      return suggestions;
    } catch (error) {
      console.error('Error handling data change:', error);
      return [];
    }
  }
}

export default MilestoneCompletionDetectionService;